import React, { memo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Star } from 'lucide-react';

const MAX = 5;

interface IRatingStarsProps {
  /** Current rating 0-5 (0 = unrated). */
  value: number;
  /** Called with the chosen rating (1-5), or 0 when the current star is
   * clicked again to clear it. Omit to render a read-only display. */
  onChange?: (value: number) => void;
  /** Pixel size of each star. */
  size?: number;
  /** Disable interaction (in-flight save). */
  disabled?: boolean;
  /** Accessible label for the whole control. */
  ariaLabel?: string;
}

/**
 * A 1-5 star rating control (spec 0007). Read-only when `onChange` is omitted.
 * Clicking a star sets that rating; clicking the currently-selected star clears
 * it back to 0 (the backend treats 0 as «clear»). Hovering previews the value.
 */
export const RatingStars = memo(
  ({ value, onChange, size = 20, disabled, ariaLabel }: IRatingStarsProps) => {
    const [hover, setHover] = useState(0);
    const readOnly = !onChange || Boolean(disabled);
    const shown = hover || value;

    return (
      <Row role="radiogroup" aria-label={ariaLabel}>
        {Array.from({ length: MAX }, (_, i) => i + 1).map((star) => {
          const active = star <= shown;
          return (
            <StarButton
              key={star}
              type="button"
              $readOnly={readOnly}
              $active={active}
              aria-label={`${star}`}
              aria-checked={star === value}
              role="radio"
              disabled={disabled}
              onMouseEnter={() => !readOnly && setHover(star)}
              onMouseLeave={() => !readOnly && setHover(0)}
              onClick={() => {
                if (readOnly || !onChange) return;
                onChange(star === value ? 0 : star);
              }}
            >
              <Star
                size={size}
                strokeWidth={2}
                fill={active ? 'currentColor' : 'none'}
              />
            </StarButton>
          );
        })}
      </Row>
    );
  },
);

RatingStars.displayName = 'RatingStars';

const Row = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const StarButton = styled.button<{ $readOnly: boolean; $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: 0;
  background: transparent;
  color: ${({ $active }) => ($active ? '#f59e0b' : '#cbd5e1')};
  cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
  transition: color 0.12s, transform 0.12s;

  ${({ $readOnly }) =>
    !$readOnly &&
    css`
      &:hover {
        transform: scale(1.1);
      }
    `}

  &:disabled {
    cursor: default;
  }

  svg {
    display: block;
  }
`;
